import { createContext, useContext, type ReactNode } from 'react'
import type { CommentThread, BlockViewer } from '@w/content-blocks'
import type { ComponentProps } from 'react'
import type { BlockEditorReviewConfig } from '../../types/review.types'
import type { CoreEditorProps } from '../core-editor'
import type { CoreViewApi } from '../view-api'

export type ReviewThread = CommentThread & { anchorBlockId?: string; selectedText?: string }
export interface ReviewContextValue {
  renderEditor?: (props: CoreEditorProps) => ReactNode
  review?: BlockEditorReviewConfig
  comments?: readonly ReviewThread[]
  spaceId?: string
  resolvePage?: ComponentProps<typeof BlockViewer>['resolvePage']
}
const ReviewContext = createContext<ReviewContextValue>({})
/** Named fields, no rest object or `??=`: the React Compiler then keeps the value's identity until an input changes. A
 * value that changed on every editor render made React search every memoized row for consumers on each keystroke. */
export function ReviewProvider({ children, renderEditor, review, comments, spaceId, resolvePage }: ReviewContextValue & { children: ReactNode }) {
  const inherited = useContext(ReviewContext)
  const value: ReviewContextValue = { renderEditor: renderEditor ?? inherited.renderEditor, review, comments, spaceId, resolvePage }
  // oxlint-disable-next-line react/jsx-no-constructed-context-values -- React Compiler owns memoization; no manual performance hooks
  return <ReviewContext value={value}>{children}</ReviewContext>
}
export const useReview = () => useContext(ReviewContext)
export const ReviewApiContext = createContext<CoreViewApi | null>(null)
export function useReviewApi() {
  const api = useContext(ReviewApiContext)
  if (!api) throw new Error('Review overlay requires CoreViewApi')
  return api
}

export function blockElement(root: HTMLElement | null, id: string): HTMLElement | null {
  return root?.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(id)}"], [data-review-id="${CSS.escape(id)}"]`) ?? null
}
